import React from 'react';

// Allow runtime override for dev/prod mode testing
let debugModeOverride: boolean | null = null;
let modeChangeListeners: (() => void)[] = [];

export const debug = {
  // Mode checking with override support
  isDev: () => debugModeOverride !== null ? debugModeOverride : import.meta.env.DEV,
  
  // Toggle for testing dev/prod modes
  setDevMode: (enabled: boolean) => {
    debugModeOverride = enabled;
    console.log(`[DEBUG] Mode switched to: ${enabled ? 'DEVELOPMENT' : 'PRODUCTION'}`);
    // Notify all listeners
    modeChangeListeners.forEach(listener => listener());
    // Force a small delay and refresh again to ensure all components update
    setTimeout(() => {
      modeChangeListeners.forEach(listener => listener());
    }, 50);
  },
  
  // Reset to environment default
  resetMode: () => {
    debugModeOverride = null;
    console.log(`[DEBUG] Mode reset to environment default: ${import.meta.env.DEV ? 'DEVELOPMENT' : 'PRODUCTION'}`);
    // Notify all listeners
    modeChangeListeners.forEach(listener => listener());
  },

  // Subscribe to mode changes
  onModeChange: (listener: () => void) => {
    modeChangeListeners.push(listener);
    return () => {
      modeChangeListeners = modeChangeListeners.filter(l => l !== listener);
    };
  },
  
  // Logging functions that respect override
  log: (...args: unknown[]) => debug.isDev() ? console.log('[DEBUG]', ...args) : undefined,
  warn: (...args: unknown[]) => debug.isDev() ? console.warn('[DEBUG]', ...args) : undefined,
  error: (...args: unknown[]) => console.error('[DEBUG]', ...args), // Always log errors
  
  // Conditional rendering helpers
  when: (condition: boolean, component: React.ReactNode): React.ReactNode => 
    condition ? component : null,
  
  // Development-only component wrapper with override support
  dev: (component: React.ReactNode): React.ReactNode => 
    debug.isDev() ? component : null,
  
  // Performance timing with override support
  time: (label: string) => debug.isDev() ? console.time(label) : undefined,
  timeEnd: (label: string) => debug.isDev() ? console.timeEnd(label) : undefined,

  // Group logging with override support
  group: (label: string) => debug.isDev() ? console.group(label) : undefined,
  groupEnd: () => debug.isDev() ? console.groupEnd() : undefined,

  // Table logging with override support
  table: (data: unknown) => debug.isDev() ? console.table(data) : undefined,
};

// Type guard for debug mode
export function isDebugMode(): boolean {
  return debug.isDev();
}

// Performance measurement hook
export function measurePerformance<T extends (...args: unknown[]) => unknown>(
  fn: T,
  label: string
): T {
  if (!import.meta.env.DEV) {
    return fn;
  }

  return ((...args: unknown[]) => {
    debug.time(label);
    const result = fn(...args);
    debug.timeEnd(label);
    return result;
  }) as T;
}

// Debug component wrapper
export function withDebugInfo<P extends object>(
  Component: React.ComponentType<P>,
  displayName: string
): React.ComponentType<P> {
  if (!debug.isDev()) {
    return Component;
  }

  const DebugWrapper = (props: P) => {
    React.useEffect(() => {
      debug.log(`${displayName} mounted`);
      return () => debug.log(`${displayName} unmounted`);
    }, []);

    React.useEffect(() => {
      debug.log(`${displayName} props changed:`, props);
    });

    return React.createElement(Component, props);
  };

  DebugWrapper.displayName = `Debug(${displayName})`;
  return DebugWrapper;
}

// Make debug functions globally available in development
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__FLOURI_DEBUG__ = {
    setDevMode: debug.setDevMode,
    resetMode: debug.resetMode,
    isDev: debug.isDev,
    log: debug.log,
    warn: debug.warn,
    error: debug.error,
    // Convenience shortcuts
    dev: () => debug.setDevMode(true),
    prod: () => debug.setDevMode(false),
    toggle: () => debug.setDevMode(!debug.isDev()),
    // Force refresh if components get stuck
    forceRefresh: () => window.location.reload(),
  };
  
  console.log('🚀 Flouri Debug Utils Available:');
  console.log('  __FLOURI_DEBUG__.dev()     - Quick switch to dev mode');
  console.log('  __FLOURI_DEBUG__.prod()    - Quick switch to prod mode');
  console.log('  __FLOURI_DEBUG__.toggle()  - Toggle between modes');
  console.log('  __FLOURI_DEBUG__.isDev()   - Check current mode');
  console.log('  __FLOURI_DEBUG__.resetMode() - Reset to environment default');
}